import { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useDispatch } from "react-redux";
import { setTokens } from "../../features/auth/authSlice";
import { useLoginMutation, useLazyMeQuery } from "../../features/auth/authApi";
import AuthPageLayout from "../../components/layout/AuthPageLayout";

export default function LoginPage() {
  const nav = useNavigate();
  const dispatch = useDispatch();

  const [login, { isLoading }] = useLoginMutation();
  const [triggerMe] = useLazyMeQuery();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorText(null);
    try {
      const res = await login({ email, password }).unwrap();
      dispatch(setTokens({ access: res.access, refresh: res.refresh }));
      await triggerMe().unwrap();
      nav("/app/dashboard", { replace: true });
    } catch (err: any) {
      setErrorText(err?.data?.detail || "Login failed. Check your email and password.");
    }
  };

  return (
    <AuthPageLayout
      title="Sign in"
      subtitle="Access the operations dashboard."
      maxWidth={420}
    >
      <Box component="form" onSubmit={onSubmit} noValidate>
        <Stack spacing={2.5}>
          {errorText && (
            <Alert severity="error" onClose={() => setErrorText(null)}>
              {errorText}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            autoComplete="email"
            autoFocus
            disabled={isLoading}
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            autoComplete="current-password"
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={isLoading}
            sx={{ py: 1.5, mt: 0.5 }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={22} color="inherit" sx={{ mr: 1.5 }} />
                Signing in…
              </>
            ) : (
              "Sign in"
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 0.5 }}>
            Don’t have an agency?{" "}
            <Link component={RouterLink} to="/register" underline="hover" fontWeight={600}>
              Create one
            </Link>
          </Typography>
        </Stack>
      </Box>
    </AuthPageLayout>
  );
}
